import { isReadonly, isRef, ref, toValue, watch, type Ref, type ShallowRef, type WritableComputedRef, type ComputedRef, type UnwrapRef, type WatchOptions} from 'vue'

// Define a comprehensive union of all supported Vue reactivity types
export type ComposableArg<T> =
  | T
  | (() => T)
  | Ref<T>
  | ComputedRef<T>
  | WritableComputedRef<T>
  | ShallowRef<T>

/**
 * Type guard to correctly identify Writable Refs (including WritableComputedRef and ShallowRef)
 */
function isWritableRef<T>(val: ComposableArg<T>): val is Ref<T> {
  return isRef(val) && !isReadonly(val)
}

/**
 * Internal use of useWritableRef
 */
function useWritableRefState<T>() {
  let stateRef: Ref<T | UnwrapRef<T>>

  function setRefValue(value: T) {
    if (stateRef) {
      // Future upstream updates gracefully overwrite the local writable ref
      stateRef.value = value
    } else {
      // First run: synchronously capture the value without a second evaluation
      stateRef = ref(value)
    }
  }

  function getRef() {
    return stateRef
  }

  return {
    getRef,
    setRefValue,
  }
}

/**
 * Returns a writable ref from any ref, reactive, computed, getter, or plain value.
 * Writable refs are reused, while readonly sources are kept in sync with a local ref via a watcher.
 * 
 * **Caution** 
 * 
 * By default, if you provide a `reactive` object (rather than a getter or ref):
 * - Property mutations are synced both ways because the returned ref shares the exact same reactive proxy.
 * - However, completely reassigning the returned ref (e.g., `myRef.value = {...}`) will break this link. It will NOT update the original reactive object, and it will stop reflecting upstream changes.
 * - To ensure that deep upstream mutations to the original reactive object will overwrite any local reassignments, pass `{ deep: true }` in `watchOptions`. (Note: this may incur a performance penalty on large objects).
 * 
 * @see https://vuejs.org/guide/essentials/watchers.html#deep-watchers
 */
export function useWritableRef<T>(initial: ComposableArg<T>, watchOptions?: Omit<WatchOptions, 'immediate' | 'flush'>): Ref<T | UnwrapRef<T>> {
  // 1. If it is already a writable ref, shallowRef, or writable computed, reuse it directly.
  if (isWritableRef(initial)) {
    return initial
  }

  const {
    getRef,
    setRefValue,
  } = useWritableRefState<T>()

  // 2. Trust Vue to evaluate the getter exactly once to collect dependencies.
  // Using `immediate: true` and `flush: sync` forces this callback to run synchronously right now.
  watch(
    () => toValue(initial),
    (newValue) => setRefValue(newValue),
    { ...watchOptions, immediate: true, flush: 'sync' }
  )

  return getRef()
}
